/**
 * ModuleView — trainee content viewer for a single module
 *
 * Steps through content items in order. Each item shows:
 * - Title, learning objective, main content (rich HTML)
 * - Media (video/image) if present
 * - Inline knowledge check quiz if question_text exists
 *
 * Navigation: prev/next between items within the module.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { toEmbedUrl, isImageUrl, isYouTubeUrl } from '../../../utils/mediaUrl';
import { PdfModal } from '../../PdfModal';

function isPdfUrl(url: string): boolean {
  return /\.pdf(\?|#|$)/i.test(url);
}

interface MediaRow {
  id: string;
  content_id: string;
  url: string;
  caption: string | null;
  display_order: number;
}

interface ContentItem {
  id: string;
  content_code: string;
  module_title?: string;
  tip_text?: string;
  learning_objective?: string;
  question_text?: string;
  option_a?: string;
  option_b?: string;
  option_c?: string;
  option_d?: string;
  correct_option?: string;
  video_url?: string;
  image_url?: string;
  read_time_minutes?: number;
}

interface ModuleContentLink {
  content_id: string;
  display_order: number;
}

interface ModuleData {
  id: string;
  name: string;
  description?: string;
  duration?: number;
  cert_training_module_content: ModuleContentLink[];
}

interface ModuleViewProps {
  moduleId: string;
  onBack: () => void;
  onComplete?: () => void;
  /** When false, hide the back affordance and the final "Done" button —
   *  used when the visitor arrived via a direct share link with no
   *  meaningful destination to return to. */
  showBack?: boolean;
  accentColor?: string;
}

function ModuleView({ moduleId, onBack, onComplete, showBack = true, accentColor = '#772cbb' }: ModuleViewProps) {
  const { supabase, user } = useAuth();
  const [module, setModule] = useState<ModuleData | null>(null);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [media, setMedia] = useState<MediaRow[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quizAnswer, setQuizAnswer] = useState<string | null>(null);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    loadModule();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load on moduleId change
  }, [moduleId]);

  async function loadModule() {
    setLoading(true);
    setCurrentIndex(0);
    setQuizAnswer(null);
    setQuizSubmitted(false);

    const { data: modData } = await supabase
      .from('cert_training_modules')
      .select('id, name, description, duration, cert_training_module_content(content_id, display_order)')
      .eq('id', moduleId)
      .single();

    if (!modData) {
      setLoading(false);
      return;
    }

    setModule(modData as ModuleData);

    // Get content IDs in display order
    const links = (modData.cert_training_module_content || [])
      .sort((a: ModuleContentLink, b: ModuleContentLink) => a.display_order - b.display_order);
    const contentIds = links.map((l: ModuleContentLink) => l.content_id);

    if (contentIds.length === 0) {
      setContentItems([]);
      setLoading(false);
      return;
    }

    const [{ data: items }, { data: mediaRows }] = await Promise.all([
      supabase
        .from('cert_training_content')
        .select('*')
        .in('id', contentIds),
      supabase
        .from('cert_training_content_media')
        .select('id, content_id, url, caption, display_order')
        .in('content_id', contentIds)
        .order('display_order', { ascending: true }),
    ]);

    // Sort by the display_order from the links
    const sorted = contentIds
      .map((id: string) => (items || []).find((item: ContentItem) => item.id === id))
      .filter(Boolean) as ContentItem[];

    setContentItems(sorted);
    setMedia((mediaRows || []) as MediaRow[]);
    setLoading(false);
  }

  function goNext() {
    if (currentIndex < contentItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setQuizAnswer(null);
      setQuizSubmitted(false);
      window.scrollTo(0, 0);
    }
  }

  function goPrev() {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setQuizAnswer(null);
      setQuizSubmitted(false);
      window.scrollTo(0, 0);
    }
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>Loading module...</div>;
  }

  if (!module) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>Module not found.</div>;
  }

  if (contentItems.length === 0) {
    return (
      <div>
        {showBack && (
          <button onClick={onBack} style={backButtonStyle}>&larr; Back</button>
        )}
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>
          This module has no content yet.
        </div>
      </div>
    );
  }

  const item = contentItems[currentIndex]!;
  const isLast = currentIndex === contentItems.length - 1;
  const isFirst = currentIndex === 0;
  const hasQuiz = !!item?.question_text;

  if (!item) return null;

  return (
    <div>
      {showBack && (
        <button onClick={onBack} style={backButtonStyle}>&larr; Back</button>
      )}

      {/* Module header + progress */}
      <div style={{
        background: 'var(--color-white)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem',
        border: '1px solid #e5e7eb', marginBottom: '1rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.5rem',
      }}>
        <div>
          <h2 className="text-lg fw-600 m-0">{module.name}</h2>
        </div>
        <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
          {currentIndex + 1} of {contentItems.length}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        height: '4px', background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)',
        marginBottom: '1.5rem', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', borderRadius: 'var(--radius-sm)',
          background: accentColor, transition: 'width 0.3s',
          width: `${((currentIndex + 1) / contentItems.length) * 100}%`,
        }} />
      </div>

      {/* Content card */}
      <div style={{
        background: 'var(--color-white)', borderRadius: 'var(--radius-lg)', padding: '2rem',
        border: '1px solid #e5e7eb',
      }}>
        {/* Title */}
        <h3 style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-3xl)', fontWeight: 600 }}>
          {item.module_title || item.content_code}
        </h3>

        {/* Learning objective */}
        {item.learning_objective && (
          <div style={{
            padding: '0.75rem 1rem', background: 'var(--color-bg)',
            borderRadius: 'var(--radius)', marginBottom: '1.5rem', marginTop: '0.75rem',
            fontSize: 'var(--text-base)', color: '#4b5563', fontStyle: 'italic',
            borderLeft: `3px solid ${accentColor}`,
          }}>
            {item.learning_objective}
          </div>
        )}

        {/* Media — new multi-item model */}
        {(() => {
          const attached = media.filter((m) => m.content_id === item.id);
          const fallback: Array<{ id: string; url: string; caption: string | null }> = [];
          if (attached.length === 0) {
            if (item.video_url) fallback.push({ id: 'legacy-video', url: item.video_url, caption: null });
            if (item.image_url) fallback.push({ id: 'legacy-image', url: item.image_url, caption: null });
          }
          const rows = attached.length ? attached : fallback;
          return rows.map((m) => (
            <div className="mb-xl" key={m.id}>
              {isYouTubeUrl(m.url) ? (
                <iframe
                  src={toEmbedUrl(m.url)}
                  style={{ width: '100%', height: '360px', border: 'none', borderRadius: 'var(--radius)' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : isImageUrl(m.url) ? (
                <img
                  src={m.url}
                  alt={m.caption || item.module_title || ''}
                  style={{ maxWidth: '100%', borderRadius: 'var(--radius)' }}
                />
              ) : isPdfUrl(m.url) ? (
                <button
                  type="button"
                  onClick={() => setPdfPreview({ url: m.url, title: m.caption || 'Resource' })}
                  style={{ display: 'inline-block', padding: '0.5rem 0.75rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-base)', color: accentColor, border: 'none', cursor: 'pointer' }}
                >
                  {m.caption || 'Open linked resource ↗'}
                </button>
              ) : (
                <a
                  href={m.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-block', padding: '0.5rem 0.75rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-base)', color: accentColor, textDecoration: 'none' }}
                >
                  {m.caption || 'Open linked resource ↗'}
                </a>
              )}
              {m.caption && !isImageUrl(m.url) && (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '0.25rem' }}>{m.caption}</div>
              )}
            </div>
          ));
        })()}

        {/* Main content (rich HTML) */}
        {item.tip_text && (
          <div
            className="training-content-body"
            style={{ fontSize: 'var(--text-lg)', lineHeight: 1.7, color: 'var(--color-sidebar-hover)' }}
            dangerouslySetInnerHTML={{ __html: item.tip_text }}
          />
        )}

        {/* Read time */}
        {item.read_time_minutes && (
          <div style={{ marginTop: '1rem', fontSize: 'var(--text-sm)', color: 'var(--color-text-light)' }}>
            Estimated read time: {item.read_time_minutes} min
          </div>
        )}

        {/* Knowledge check quiz — only shown to signed-in viewers. Anonymous
            share-link visitors (property staff) just read the content; no quiz,
            no answer-key exposure, no scoring expectations. */}
        {hasQuiz && user && (
          <div style={{
            marginTop: '2rem', padding: '1.5rem',
            background: '#fafafa', borderRadius: 'var(--radius-lg)',
            border: '1px solid #e5e7eb',
          }}>
            <div style={{
              fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.5px', color: accentColor, marginBottom: '0.75rem',
            }}>
              Knowledge Check
            </div>
            <div style={{ fontWeight: 500, marginBottom: '1rem', lineHeight: 1.5 }}>
              {item.question_text}
            </div>

            {['A', 'B', 'C', 'D'].map(opt => {
              const optionText = item[`option_${opt.toLowerCase()}` as keyof ContentItem] as string;
              if (!optionText) return null;

              const isSelected = quizAnswer === opt;
              const isCorrect = opt === item.correct_option;
              const showResult = quizSubmitted;

              let borderColor = 'var(--color-border)';
              let bg = 'var(--color-white)';
              if (showResult && isCorrect) { borderColor = '#16a34a'; bg = 'var(--color-success-bg)'; }
              else if (showResult && isSelected && !isCorrect) { borderColor = '#dc2626'; bg = 'var(--color-error-bg)'; }
              else if (isSelected) { borderColor = accentColor; bg = `${accentColor}08`; }

              return (
                <div
                  key={opt}
                  onClick={() => { if (!quizSubmitted) setQuizAnswer(opt); }}
                  style={{
                    padding: '0.75rem 1rem', borderRadius: 'var(--radius)',
                    border: `2px solid ${borderColor}`, marginBottom: '0.5rem',
                    cursor: quizSubmitted ? 'default' : 'pointer',
                    background: bg, transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                  }}
                >
                  <span style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 'var(--text-sm)', fontWeight: 600, flexShrink: 0,
                    background: isSelected ? accentColor : 'var(--color-bg)',
                    color: isSelected ? 'var(--color-white)' : 'var(--color-text-light)',
                  }}>
                    {opt}
                  </span>
                  <span className="text-sm">{optionText}</span>
                </div>
              );
            })}

            {quizAnswer && !quizSubmitted && (
              <button
                onClick={() => setQuizSubmitted(true)}
                style={{
                  marginTop: '0.75rem', padding: '0.625rem 1.5rem',
                  background: accentColor, color: 'var(--color-white)', border: 'none',
                  borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 500,
                  fontSize: 'var(--text-base)',
                }}
              >
                Check Answer
              </button>
            )}

            {quizSubmitted && (
              <div style={{
                marginTop: '0.75rem', padding: '0.75rem 1rem',
                borderRadius: 'var(--radius)', fontSize: 'var(--text-base)',
                background: quizAnswer === item.correct_option ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
                color: quizAnswer === item.correct_option ? 'var(--color-success)' : 'var(--color-error)',
                fontWeight: 500,
              }}>
                {quizAnswer === item.correct_option
                  ? 'Correct!'
                  : `The correct answer is ${item.correct_option}.`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: '1.5rem', gap: '1rem',
      }}>
        <button
          onClick={goPrev}
          disabled={isFirst}
          style={{
            ...navButtonStyle,
            opacity: isFirst ? 0.4 : 1,
            cursor: isFirst ? 'default' : 'pointer',
          }}
        >
          &larr; Previous
        </button>

        {isLast ? (
          showBack ? (
            <button
              onClick={async () => {
                // Log module completion only for signed-in operators / ambassadors.
                // Anonymous share-link viewers with no back destination see
                // 'End of module' below instead — no log, no tracking.
                if (user?.id) {
                  await supabase.from('cert_training_log').insert({
                    operator_id: user.id,
                    event_type: 'module_complete',
                    module_id: moduleId,
                    created_at: new Date().toISOString(),
                  });
                }
                if (onComplete) onComplete();
                onBack();
              }}
              style={{ ...navButtonStyle, background: accentColor, color: 'var(--color-white)', borderColor: accentColor }}
            >
              {user ? 'Complete Module' : 'Done'}
            </button>
          ) : (
            <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-success)', fontWeight: 500 }}>
              ✓ End of module
            </span>
          )
        ) : (
          <button onClick={goNext} style={{ ...navButtonStyle, background: accentColor, color: 'var(--color-white)', borderColor: accentColor }}>
            Next &rarr;
          </button>
        )}
      </div>

      {pdfPreview && (
        <PdfModal url={pdfPreview.url} title={pdfPreview.title} onClose={() => setPdfPreview(null)} />
      )}
    </div>
  );
}

const backButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-light)', fontSize: 'var(--text-base)', padding: '0 0 1rem',
  display: 'flex', alignItems: 'center', gap: '6px',
};

const navButtonStyle: React.CSSProperties = {
  padding: '0.625rem 1.5rem', borderRadius: 'var(--radius)',
  border: '1px solid #e5e7eb', background: 'var(--color-white)',
  fontSize: 'var(--text-base)', fontWeight: 500, cursor: 'pointer',
};

export default ModuleView;
